/**
 * `#/identities`：身分區唯一的入口（T02，四區重規劃）。
 *
 * 版面固定三段，順序＝使用者由上往下讀的順序：
 * 1. **摘要條**（sticky）：登入者、憑證可用／總數。數字只從 `c.poolAccounts`
 *    算一次，跟頂列 `topbar-pool-health` 同一份資料、同一套判定（`ok === true` 才算可用），
 *    不在這一頁另打 API（PLAN R3：`useInbox` 只在 App 直下呼叫一次）。
 * 2. **身分表**：`IdentityTable` 一列一顆身分，四個狀態同列（憑證／持有／在線／最後活動），
 *    表格正上方是篩選條（全部／待處理／在線＋搜尋＋一句對照說明）。三態（loading／empty／error）在這裡，
 *    因為它們跟資料來源有關、跟表格怎麼畫無關。
 * 3. **選取工具列**：全選／已選 n／批次動作。按鈕跟著表格的勾選走，勾選來源就是上面那張表。
 *
 * 舊網址（`#/accounts`、`#/accounts/sessions`、`#/accounts/creds`、`#/admin/accounts`）
 * 由 `shell/routes.ts` 正規化成 `#/identities`，所以這一頁不再有分頁。
 */
import { SessionBar, DataStateNotice, resolveDataState, errorKindOf, type SessionBarProps } from '@chat-rea/console-ui';
import { useMemo, useState } from 'react';
// `mergeIdentityRows` 是本批新增的（`packages/console-ui/src/identity-rows.ts`），但
// `@chat-rea/console-ui` 的型別／執行檔仍是 `dist/`，本批規定不 build，所以直接吃 source。
// dist 追上後這一行換回套件匯入即可（介面一模一樣）。
import {
  mergeIdentityRows,
  type IdentityRow,
} from '../../../../packages/console-ui/src/identity-rows';
import type { ConsoleActions } from '../hooks/useConsoleActions';
import { NoticeStack } from '@chat-rea/console-ui';
import { filterIdentityRows, IdentityTable } from './IdentityTable';

export interface AccountsPageProps {
  c: ConsoleActions;
  /** 登入者（auth-service）：`email · role`。沒登入就不顯示那一段。 */
  viewer?: { email: string; role: string };
  /** 「登入已過期」那一條（`email: ''` 時 SessionBar 只渲染重登按鈕），與其他頁一致。 */
  session?: SessionBarProps;
}

export function AccountsPage({ c, viewer, session }: AccountsPageProps) {
  const loading = c.poolLoading && c.poolAccounts.length === 0;
  /**
   * 四態統一走 `resolveDataState`（與其他清單同一支規則）：401／斷線不再長成「還沒有帳號」，
   * 這頁以前自己寫一套 loading/failed/empty，正好漏掉「登入失效」那一態。
   */
  const dataState = resolveDataState({
    loading: c.poolLoading,
    error: c.poolError,
    errorKind: c.poolError ? errorKindOf(c.poolError) : null,
    count: c.poolAccounts.length,
  });

  const total = c.poolAccounts.length;
  const usable = c.poolAccounts.filter((account) => account.ok === true).length;

  /**
   * 身分列：憑證與 session 在這裡 join 一次，表格與工具列**共用同一份 `rows`**，
   * 所以「已選 n」算的是畫面上真的勾得到的列，不是另一個陣列的長度。
   */
  const rows = useMemo(
    () => mergeIdentityRows(c.poolAccounts, c.conversations),
    [c.poolAccounts, c.conversations],
  );
  /**
   * 「待處理」＝身分層面有狀況的列（缺憑證／憑證壞），跟篩選條上的數字**同一份規則**。
   * 跟上面的 `usable`（憑證能不能用）是兩件事，所以兩個數字各自都有明確的主詞。
   */
  const pending = useMemo(() => filterIdentityRows(rows, 'issues', '').length, [rows]);
  const pickedSet = useMemo(() => new Set(c.sessionPicked), [c.sessionPicked]);
  /** 只有帶 session 的列能被選（批次動作用的 id 是 session id）。 */
  const selectable = useMemo(
    () => rows.filter((row) => row.sessionId !== null),
    [rows],
  );
  const selected = selectable.filter((row) => row.sessionId !== null && pickedSet.has(row.sessionId)).length;
  const allSelected = selectable.length > 0 && selected === selectable.length;
  const [actionError, setActionError] = useState<string | null>(null);
  const busy = c.busy || c.opening !== null;

  /**
   * 全選／清除選取：`c` 只給 `toggleSessionPick`（單顆切換），所以逐顆切換；
   * 這樣不必為了「全選」在 `useConsoleActions` 再開一個 state（那支檔有其他隻在改）。
   */
  const toggleAll = () => {
    const targets = allSelected
      ? selectable
      : selectable.filter((row) => row.sessionId !== null && !pickedSet.has(row.sessionId));
    for (const row of targets) {
      if (row.sessionId !== null) c.toggleSessionPick(row.sessionId);
    }
  };

  const actionLabel = (row: IdentityRow): string => {
    if (row.state === 'no-cred') return '補憑證';
    if (row.sessionId !== null) return '換身分';
    return '開啟';
  };

  /** 每列單顆動作：有 session 就切過去；沒有就從池子用它開一條。 */
  const runRowAction = (row: IdentityRow) => {
    setActionError(null);
    if (row.sessionId !== null) {
      c.openConversation(row.sessionId);
      return;
    }
    void c.usePoolAccount(row.username).catch((err: unknown) => {
      setActionError(err instanceof Error ? err.message : String(err));
    });
  };

  return (
    <>
      {session ? <SessionBar {...session} /> : null}
      <NoticeStack
        notices={c.notices}
        onDismiss={c.dismissNotice}
        onRetry={(notice) => {
          if (notice.retry) notice.retry();
          c.dismissNotice(notice.id);
        }}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-3" data-testid="accounts-page">
        {/* ① 摘要條：一行看完「我是誰、憑證幾顆能用」。身分層面的「待處理」交給篩選條。 */}
        <section
          className="sticky top-0 z-10 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface px-3 py-2 text-[11px]"
          aria-label="帳號摘要"
          data-testid="accounts-summary"
        >
          <span className="flex min-w-0 items-center gap-1">
            <span className="text-text-tertiary">登入者</span>
            <span className="truncate font-medium" data-testid="accounts-summary-viewer">
              {viewer ? `${viewer.email} · ${viewer.role}` : '未登入'}
            </span>
          </span>
          {/* 只講「憑證」：這格算的是憑證能不能用，不是身分層面的狀況。
              「身分層面幾顆待處理」在篩選條上（待處理 n）＋一句對照說明，兩個數字才對得起來。 */}
          <span
            className="flex items-center gap-1"
            title="憑證可用／總數：只算 ok === true 的憑證，與頂列同一份"
            data-testid="accounts-summary-pool"
          >
            <span className="text-text-tertiary">憑證</span>
            <span className="font-medium">
              {loading ? '載入中' : `${usable}/${total}`}
            </span>
            {!loading && total > 0 ? <span className="text-text-tertiary">可用</span> : null}
          </span>
          <span className="ml-auto hidden text-[10px] text-text-tertiary sm:inline">
            憑證與在線同一份 join（見本表）
          </span>
        </section>

        {/* ② 身分表：四狀態同列；表格上方是篩選條（全部／在線／待處理）。
            `shrink-0`：這塊改成卡片列之後高度會超過視窗，若讓 flex 壓縮它，外框的
            overflow 就會把第 5 列之後整段裁掉（外層 scrollHeight == clientHeight、捲不動）。
            高度照內容長，捲動交給 `data-testid="accounts-page"` 那一層（overflow-y-auto）。 */}
        <section className="flex shrink-0 flex-col rounded-md border border-border" aria-label="身分表">
          {dataState !== 'ready' ? (
            <div className="p-3">
              <DataStateNotice
                state={dataState}
                message={c.poolError}
                onRetry={() => void c.loadPool()}
                emptyText="還沒有帳號"
                emptyHint="池子是空的——註冊一顆憑證後，這裡會列出每一顆身分的憑證／session／在線狀態。"
                loadingText="正在載入帳號…"
                testId="accounts-state"
                narrow
              />
            </div>
          ) : null}
          {c.poolAccounts.length > 0 ? (
            /* 不要 `overflow-hidden`：它把卡片列裁成固定高度（手機量到 613px、內容 900px+），
               最後幾張卡片因此永遠看不到也捲不到。圓角交給 section 的邊框即可。 */
            <div className="m-2 rounded border border-border sm:m-3" data-testid="identity-table-wrap">
              <IdentityTable
                rows={rows}
                picked={c.sessionPicked}
                onTogglePick={c.toggleSessionPick}
                onAction={runRowAction}
                actionLabel={actionLabel}
                busy={busy}
                hint={
                  loading || total === 0
                    ? null
                    : `憑證 ${usable}/${total} · 待處理 ${pending}（身分層面，不是憑證）`
                }
              />
            </div>
          ) : null}
        </section>

        {/* ③ 選取工具列：勾選來自上面的身分表格（同一份 selected 狀態）。 */}
        <section
          className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2"
          style={{ borderColor: 'var(--ak-color-border)' }}
          aria-label="選取工具"
          data-testid="accounts-selection-bar"
        >
          <button
            type="button"
            className="cui-btn cui-btn--sm"
            disabled={selectable.length === 0 || busy}
            title={selectable.length === 0 ? '沒有可批次處理的 session' : allSelected ? '取消全選' : '選取全部有 session 的身分'}
            onClick={toggleAll}
            data-testid="accounts-select-all"
            aria-pressed={allSelected}
          >
            {allSelected ? '取消全選' : '全選'}
          </button>
          <span className="text-[11px] text-text-tertiary" data-testid="accounts-selected-count">
            已選 {selected}
            <span className="text-text-tertiary/70"> / {selectable.length}</span>
          </span>
          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
          <button
            type="button"
            className="cui-btn cui-btn--sm"
            disabled={busy || selectable.length === 0}
            onClick={() => void c.joinSessions(c.targetRoom)}
            title="把勾選的（沒勾＝全部）送進目標房"
          >
            全部進房
          </button>
          <button
            type="button"
            className="cui-btn cui-btn--sm cui-btn--danger"
            disabled={busy || selectable.length === 0}
            onClick={() => void c.leaveAll()}
            title="逐顆關閉 session（不刪帳號）"
            data-testid="accounts-leave-all"
          >
            全部離開
          </button>
          <button
            type="button"
            className="cui-btn cui-btn--sm cui-btn--ghost"
            disabled={selected === 0}
            onClick={toggleAll}
            data-testid="accounts-clear-selection"
          >
            清除選取
          </button>
        </section>

        {/* 單顆動作失敗（找不到 session／開通失敗）就地講，不吞掉。 */}
        {actionError !== null ? (
          <p className="break-words text-[11px] text-danger" role="alert" data-testid="accounts-action-error">
            {actionError}
          </p>
        ) : null}
      </div>
    </>
  );
}
